import type { ReactNode } from 'react'

import { HelpIconTooltip } from '@gto/ui/components/help-icon-tooltip'
import type { DataTableColumnMeta } from '@gto/ui/data-table'

/**
 * Column width + alignment helpers.
 *
 * Every column in this app is equal-weight, so `grow` alone is the whole story — uneven
 * weighting would need `meta.growFactor`, which nothing here asks for.
 */

type ColumnWidthOptions = {
  /** For a fluid column, both the floor and the flex basis; for a fixed one, simply the width. */
  min: number
  align?: DataTableColumnMeta['align']
  /** Align the header independently of the body. */
  headerAlign?: DataTableColumnMeta['headerAlign']
}

const column = ({ min, align, headerAlign }: ColumnWidthOptions, grow: boolean) => ({
  size: min,
  minSize: min,
  // Users were never able to resize these columns, and a grow column that also resizes
  // fights the auto-fill.
  enableResizing: false,
  meta: {
    ...(grow ? { grow: true } : {}),
    align,
    headerAlign,
  } satisfies DataTableColumnMeta,
})

/** A width-filling column: shares the spare width equally with the other fluid columns. */
export const fluidColumn = (options: ColumnWidthOptions) => column(options, true)

/** No `grow`, so it holds its width while the fluid columns absorb the slack. */
export const fixedColumn = (options: ColumnWidthOptions) => column(options, false)

/**
 * A column `header` carrying an explanation.
 *
 * Only the icon is the trigger, and it swallows its own click: the DS hangs the sort toggle on
 * the columnheader element, so a hint inside a sortable header would re-sort on the way to the
 * tooltip. Sorting is off app-wide today, but a future table can lift that default.
 */
export const headerWithHint = (label: string, hint: ReactNode) => () => (
  <span
    className="inline-flex items-center gap-1"
    onClick={(event) => event.stopPropagation()}
  >
    {label}
    <HelpIconTooltip content={hint} />
  </span>
)
